import ErrorFeedback from 'Components/ErrorFeedback/ErrorFeedback'
import PageContainer from 'Components/PageContainer'
import evalCondition from 'Helpers/evalCondition'
import { notifyParent } from 'Helpers/notifyParent'
import {
  Button,
  BUTTON_SIZE_LG,
  BUTTON_VARIANT_PRIMARY,
  EmptyState,
  Flex,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Loading,
  MESSAGE_BOX_VARIANT_HELP,
  MessageBox,
  NoResults,
  ToastContainer,
  Tooltip
} from 'mailup-web-components'
import emptyStateImage from 'mailup-web-components/resources/mailup-web-app/img/svg/empty-state-tasks.svg'
import Header from 'Pages/TasksList/components/Header/Header'
import {
  copyTriggerCallback,
  deleteTaskCallback,
  resetSearchCallback,
  searchTasksCallback
} from 'Pages/TasksList/recoil/callbacks/callbacks'
import { pageState } from 'Pages/TasksList/recoil/state/page'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState
} from 'recoil'

import { useHistory } from 'react-router-dom'
import { ModalActionCopyConfirm } from './components/Modals/ModalActionCopyConfirm'
import { ModalDelete } from './components/Modals/ModalDelete'
import { ModalSimulate } from './components/Modals/ModalSimulate'
import { ModalStatistics } from './components/Modals/ModalStatistics'
import Results from './components/Results'
import Search from './components/Search'
import {
  modalCopy,
  modalDelete,
  modalSimulate,
  modalStatistics
} from './recoil/state/modals'
import { searchResult } from './recoil/state/search'

const TasksList = () => {
  const intl = useIntl()
  const history = useHistory()
  const { loading, loaded, error, severity, canCreateTrigger, isEmptyState } =
    useRecoilValue(pageState)
  const goToMShop = evalCondition(
    'uo:GEST AND NOT c:IsRetailer AND NOT c:IsDeveloper AND NOT co:TURNOFFMSHOP'
  )
  const fetchResults = useRecoilCallback(searchTasksCallback, [])
  const { items } = useRecoilValue(searchResult)
  const resetSearch = useRecoilCallback(resetSearchCallback, [])

  // modal copy
  const copyTrigger = useRecoilCallback(copyTriggerCallback, [])
  const resetCopyModal = useResetRecoilState(modalCopy)
  const {
    show: showModalCopy,
    idTask: modalCopyIdTask,
    fetchData
  } = useRecoilValue(modalCopy)
  const handleModalCopyCancel = () => {
    !fetchData && resetCopyModal()
  }
  const handleModalCopyConfirm = () => {
    !fetchData && modalCopyIdTask && copyTrigger(modalCopyIdTask)
  }

  // end modal copy

  // modal Simulate
  const { show: showModalSimulate } = useRecoilValue(modalSimulate)
  const resetModalSimulate = useResetRecoilState(modalSimulate)
  const handleModalSimulateCancel = () => {
    resetModalSimulate()
  }

  // end modal Simulate

  // modal Statistics
  const [{ show: showModalStatistics }, setModalStatistics] =
    useRecoilState(modalStatistics)
  const handleModalStatisticsCancel = () => {
    setModalStatistics((prev) => ({
      ...prev,
      show: false
    }))
  }
  const handleModalStatisticsConfirm = () => {}
  // end modal Statistics

  // modal Delete
  const deleteTask = useRecoilCallback(deleteTaskCallback, [])
  const [
    { show: showModalDelete, idTask: modalDeleteIdTask, name: modalDeleteName },
    setModalDelete
  ] = useRecoilState(modalDelete)
  const handleModalDeleteCancel = () => {
    setModalDelete((prev) => ({
      ...prev,
      show: false
    }))
  }
  const handleModalDeleteConfirm = () => {
    deleteTask(modalDeleteIdTask)
  }
  // end modal Delete

  const handleUpgradeClick = () => {
    notifyParent('redirectMainFrame', [
      '/console/mshop_redirector.aspx?t=mshopem'
    ])
  }

  useEffect(() => {
    resetSearch()
  }, [])

  useEffect(() => {
    !loaded && fetchResults(true)
  }, [loaded])

  const ButtonNewTask = () => {
    const handleSaveClick = () => {
      history.push('/automation/task')
    }

    return (
      <Flex margin='24px 0 0 0' justifyContent='flex-end'>
        <Button
          kind={BUTTON_VARIANT_PRIMARY}
          size={BUTTON_SIZE_LG}
          data-mu-test='cta-NewTask'
          onClick={handleSaveClick}
        >
          {intl.formatMessage({ id: 'Load.TriggersList.NewTriggerButton' })}
        </Button>
      </Flex>
    )
  }

  return (
    <>
      <Header />
      <Loading open={loading} />
      <ToastContainer />
      <Tooltip />
      <ModalActionCopyConfirm
        show={showModalCopy}
        handleCancel={handleModalCopyCancel}
        handleConfirm={handleModalCopyConfirm}
      />
      <ModalSimulate
        show={showModalSimulate}
        handleCancel={handleModalSimulateCancel}
      />
      <ModalStatistics
        show={showModalStatistics}
        handleCancel={handleModalStatisticsCancel}
        handleConfirm={handleModalStatisticsConfirm}
      />
      <ModalDelete
        show={showModalDelete}
        handleCancel={handleModalDeleteCancel}
        handleConfirm={handleModalDeleteConfirm}
        taskName={modalDeleteName}
      />
      <PageContainer data-mu-test='c-tasks-list'>
        <ErrorFeedback error={error} severity={severity} />
        {!error && loaded && (
          <>
            {isEmptyState ? (
              <EmptyState
                thumbSrc={emptyStateImage}
                title={intl.formatMessage({
                  id: 'Load.TriggersList.EmptyStateTitle'
                })}
                description={intl.formatMessage({
                  id: 'Load.TriggersList.EmptyStateMessage'
                })}
                buttonText={intl.formatMessage({
                  id: 'Load.TriggersList.EmptyStateBtn'
                })}
                onClickButton={() => history.push('/automation/task')}
              />
            ) : (
              <>
                <Search />
                <div className='l-section'>
                  {items.length === 0 && (
                    <NoResults
                      text={intl.formatMessage({
                        id: 'Load.SearchRecipients.noResultsTitle'
                      })}
                    />
                  )}
                  {items.length > 0 && <Results items={items} />}
                </div>
              </>
            )}
            {canCreateTrigger && !isEmptyState ? <ButtonNewTask /> : false}
            {!canCreateTrigger && (
              <MessageBox
                variant={MESSAGE_BOX_VARIANT_HELP}
                title={intl.formatMessage({
                  id: 'Load.TriggersList.TitleAlertCreateTriggerLimit'
                })}
              >
                {goToMShop ? (
                  <Flex columnGap='4px'>
                    <p>
                      {intl.formatMessage({
                        id: 'Load.TriggersList.AlertCreateTriggerLimitFirst'
                      })}
                    </p>
                    <Link
                      variant={LINK_VARIANT_HIGHLIGHT}
                      onClick={handleUpgradeClick}
                    >
                      {intl.formatMessage({
                        id: 'Load.TriggersList.UpgradePlanNew'
                      })}
                    </Link>
                    <p>
                      {intl.formatMessage({
                        id: 'Load.TriggersList.AlertCreateTriggerLimitSecond'
                      })}
                    </p>
                  </Flex>
                ) : (
                  <>
                    {intl.formatMessage({
                      id: 'Load.TriggersList.AlertCreateTriggerLimitWL'
                    })}
                  </>
                )}
              </MessageBox>
            )}
          </>
        )}
      </PageContainer>
    </>
  )
}

export default TasksList
